import { StyleSheet, css } from 'aphrodite'
import React from 'react'

interface LandingSectionProps {
  subtitle?: string
  title: string
  description: string
  imageSrc: string
  imageAlt: string
  buttonText: string
  testimonialText: string
  userCount: string
  onButtonClick: () => void
  showCallToAction?: boolean
}

export default function LandingSection({
  subtitle,
  title,
  description,
  imageSrc,
  imageAlt,
  buttonText,
  testimonialText,
  userCount,
  onButtonClick,
  showCallToAction = true,
}: LandingSectionProps) {
  return (
    <section className={css(styles.section)}>
      <div className={css(styles.container)}>
        <div className={css(styles.grid)}>
          <div className={css(styles.content)}>
            {subtitle && <h3 className={css(styles.subtitle)}>{subtitle}</h3>}
            <h2 className={css(styles.title)}>{title}</h2>
            <p className={css(styles.description)}>{description}</p>
            {showCallToAction && (
              <>
                <button onClick={onButtonClick} className={css(styles.button)}>
                  {buttonText}
                </button>
                <div className={css(styles.testimonials)}>
                  <div className={css(styles.avatars)}>
                    <img
                      src="/brokers.webp"
                      alt="User 1"
                      className={css(styles.avatar)}
                      loading="lazy"
                    />
                    <img
                      src="/subhauler.webp"
                      alt="User 2"
                      className={css(styles.avatar)}
                      loading="lazy"
                    />
                    <img
                      src="/contractors.webp"
                      alt="User 3"
                      className={css(styles.avatar)}
                      loading="lazy"
                    />
                  </div>
                </div>
              </>
            )}
          </div>
          <div className={css(styles.imageContainer)}>
            <img
              src={imageSrc}
              alt={imageAlt}
              className={css(styles.image)}
              loading="lazy"
            />
          </div>
        </div>
      </div>
    </section>
  )
}

const styles = StyleSheet.create({
  section: {
    backgroundColor: '#102E65',
    color: '#ffffff',
    maxHeight: '85vh',
    minHeight: '70vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px 0',
    '@media (min-width: 480px)': {
      padding: '30px 0',
    },
    '@media (min-width: 768px)': {
      padding: '40px 2rem',
    },
    '@media (min-width: 1024px)': {
      padding: '40px 3rem',
    },
    '@media (min-width: 1280px)': {
      padding: '40px 5rem',
    },
  },
  container: {
    margin: '0 auto',
    padding: '0 16px',
    width: '100%',
    maxWidth: '1200px',
    '@media (min-width: 480px)': {
      padding: '0 20px',
    },
    '@media (min-width: 768px)': {
      padding: '0 32px',
    },
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: '32px',
    alignItems: 'center',
    '@media (min-width: 480px)': {
      gap: '40px',
    },
    '@media (min-width: 768px)': {
      gap: '48px',
    },
    '@media (min-width: 1024px)': {
      gridTemplateColumns: '1fr 1fr',
    },
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    textAlign: 'left',
    '@media (min-width: 480px)': {
      gap: '20px',
    },
    '@media (min-width: 768px)': {
      gap: '24px',
    },
  },
  subtitle: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#D3EDFF',
    '@media (min-width: 480px)': {
      fontSize: '18px',
    },
    '@media (min-width: 768px)': {
      fontSize: '20px',
    },
  },
  title: {
    fontFamily: '"Anton", sans-serif',
    fontSize: '36px',
    lineHeight: 1.2,
    '@media (min-width: 480px)': {
      fontSize: '42px',
    },
    '@media (min-width: 768px)': {
      fontSize: '50px',
    },
    '@media (min-width: 1024px)': {
      fontSize: '60px',
    },
  },
  description: {
    fontSize: '16px',
    lineHeight: 1.6,
    color: '#E0E0E0',
    fontWeight: 400,
    '@media (min-width: 768px)': {
      fontSize: '18px',
    },
  },
  button: {
    backgroundColor: '#6DC3FF',
    color: '#1F407E',
    padding: '12px 24px',
    borderRadius: '24px',
    fontSize: '14px',
    fontWeight: 600,
    border: 'none',
    cursor: 'pointer',
    alignSelf: 'flex-start',
    transition: 'all 0.2s ease',
    ':hover': {
      backgroundColor: '#5AB5F0',
      transform: 'translateY(-1px)',
    },
    ':active': {
      transform: 'translateY(0)',
    },
    '@media (min-width: 480px)': {
      fontSize: '15px',
      padding: '12px 28px',
    },
    '@media (min-width: 768px)': {
      fontSize: '16px',
      padding: '12px 32px',
    },
  },
  testimonials: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginTop: '12px',
    '@media (min-width: 480px)': {
      gap: '14px',
      marginTop: '14px',
    },
    '@media (min-width: 768px)': {
      gap: '16px',
      marginTop: '16px',
    },
  },
  avatars: {
    display: 'flex',
  },
  avatar: {
    width: '32px',
    height: '32px',
    borderRadius: '50%',
    border: '2px solid #ffffff',
    marginLeft: '-6px',
    '@media (min-width: 480px)': {
      width: '36px',
      height: '36px',
      marginLeft: '-7px',
    },
    '@media (min-width: 768px)': {
      width: '40px',
      height: '40px',
      marginLeft: '-8px',
    },
  },
  testimonialText: {
    fontSize: '12px',
    fontWeight: 600,
    '@media (min-width: 480px)': {
      fontSize: '13px',
    },
    '@media (min-width: 768px)': {
      fontSize: '14px',
    },
  },
  userCount: {
    fontSize: '12px',
    color: '#BDDFFF',
    '@media (min-width: 480px)': {
      fontSize: '13px',
    },
    '@media (min-width: 768px)': {
      fontSize: '14px',
    },
  },
  imageContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    order: 1,
    '@media (min-width: 1024px)': {
      order: 0,
    },
  },
  image: {
    maxWidth: '100%',
    maxHeight: '50vh',
    borderRadius: '16px',
    '@media (min-width: 480px)': {
      maxHeight: '55vh',
      borderRadius: '20px',
    },
    '@media (min-width: 768px)': {
      maxHeight: '60vh',
      borderRadius: '24px',
    },
    '@media (min-width: 1024px)': {
      maxHeight: '70vh',
    },
  },
})
